import api from './api';
import { Conversation, ArchiveInfo } from '../types/message';

export interface ArchiveResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

export interface BulkArchiveResponse {
  success: boolean;
  message: string;
  data?: {
    total_requested: number;
    successfully_archived: number;
    successfully_restored: number;
    already_archived: number;
    not_archived: number;
    failed: number;
    archived_conversations?: string[];
    restored_conversations?: string[];
    errors?: Array<{
      conversation_id: string;
      error: string;
    }>;
  };
  error?: string;
}

export interface ArchivedConversation extends Conversation {
  archive_info: ArchiveInfo;
  message_count: number;
}

// Standard DRF pagination format (no custom wrapper)
export interface ArchiveListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ArchivedConversation[];
}

export interface ArchiveStatsResponse {
  success: boolean;
  data: {
    total_conversations: number;
    active_conversations: number;
    archived_conversations: number;
    archive_percentage: number;
    archive_reasons: {
      manual: number;
      bulk_action: number;
      inactivity: number;
      cleanup: number;
    };
    recent_archives: {
      today: number;
      this_week: number;
      this_month: number;
    };
    auto_restores: {
      this_week: number;
      this_month: number;
    };
  };
}

export interface UnifiedSearchResponse {
  success: boolean;
  data: {
    count: number;
    next?: string;
    previous?: string;
    results: Array<{
      id: string;
      type: 'messaging' | 'ai';
      title: string;
      archive_info: {
        archived_at: string;
        archive_reason: string;
      };
      last_activity: string;
      message_count: number;
    }>;
  };
}

export interface AnalyticsResponse {
  success: boolean;
  data: {
    period_days: number;
    messaging?: {
      total_conversations: number;
      archived: number;
      archive_rate: number;
      avg_archive_duration_days: number;
      auto_restores: number;
    };
    ai_chats?: {
      total_sessions: number;
      archived: number;
      archive_rate: number;
      avg_archive_duration_days: number;
      auto_restores: number;
    };
    trends: {
      daily_archives: number[];
      daily_restores: number[];
      top_archive_reasons: Array<{
        reason: string;
        count: number;
      }>;
    };
  };
}

class ArchiveApiService {
  // Messaging conversation archive operations
  async archiveConversation(
    conversationId: string, 
    reason: string = 'manual', 
    notes: string = ''
  ): Promise<ArchiveResponse> {
    console.log('[ArchiveAPI] === ARCHIVE CONVERSATION ===');
    console.log('[ArchiveAPI] conversationId:', conversationId);
    console.log('[ArchiveAPI] reason:', reason);
    console.log('[ArchiveAPI] Full URL:', `/messages/conversations/${conversationId}/archive/`);
    
    try {
      const response = await api.post<ArchiveResponse>(`/messages/conversations/${conversationId}/archive/`, {
        reason,
        notes
      });
      console.log('[ArchiveAPI] ===== RAW RESPONSE =====');
      console.log('[ArchiveAPI] Full response object:', JSON.stringify(response, null, 2));
      console.log('[ArchiveAPI] response.success:', response.success);
      console.log('[ArchiveAPI] response.data:', response.data);
      console.log('[ArchiveAPI] response.message:', response.message);
      console.log('[ArchiveAPI] response.error:', response.error);
      console.log('[ArchiveAPI] =======================');
      return response;
    } catch (error) {
      console.error('[ArchiveAPI] Archive error:', (error as any).response?.data || error);
      throw error;
    }
  }

  async restoreConversation(
    conversationId: string, 
    notes: string = ''
  ): Promise<ArchiveResponse> {
    const response = await api.post<ArchiveResponse>(`/messages/conversations/${conversationId}/restore/`, {
      notes
    });
    return response;
  }

  async bulkArchiveConversations(
    conversationIds: string[], 
    reason: string = 'bulk_action', 
    notes: string = ''
  ): Promise<BulkArchiveResponse> {
    const response = await api.post<BulkArchiveResponse>('/messages/conversations/bulk-archive/', {
      conversation_ids: conversationIds,
      reason,
      notes
    });
    return response;
  }

  async bulkRestoreConversations(
    conversationIds: string[], 
    notes: string = ''
  ): Promise<BulkArchiveResponse> {
    const response = await api.post<BulkArchiveResponse>('/messages/conversations/bulk-restore/', {
      conversation_ids: conversationIds,
      notes
    });
    return response;
  }

  async getArchivedConversations(params: {
    limit?: number;
    offset?: number;
    search?: string;
    archive_reason?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    order?: string;
  } = {}): Promise<ArchiveListResponse> {
    console.log('[ArchiveAPI] === GET ARCHIVED CONVERSATIONS ===');
    console.log('[ArchiveAPI] params:', params);
    
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    const queryString = queryParams.toString();
    const url = queryString ? `/messages/conversations/archived/?${queryString}` : '/messages/conversations/archived/';
    
    console.log('[ArchiveAPI] Full URL:', url);
    
    try {
      const response = await api.get<ArchiveListResponse>(url);
      console.log('[ArchiveAPI] Response received:', response);
      return response;
    } catch (error) {
      console.error('[ArchiveAPI] ERROR:', error);
      throw error;
    }
  }

  async getArchiveStats(): Promise<ArchiveStatsResponse> {
    const response = await api.get<ArchiveStatsResponse>('/messages/conversations/archive-stats/');
    return response;
  }

  async autoRestoreConversation(
    conversationId: string, 
    trigger: string = 'new_message', 
    messageId?: string
  ): Promise<ArchiveResponse> {
    const response = await api.post<ArchiveResponse>(`/messages/conversations/${conversationId}/auto-restore/`, {
      trigger,
      message_id: messageId
    });
    return response;
  }

  // Chatbot session archive operations
  async archiveChatbotSession(
    sessionId: string, 
    reason: string = 'manual', 
    notes: string = ''
  ): Promise<ArchiveResponse> {
    const response = await api.post<ArchiveResponse>(`/chatbot/sessions/${sessionId}/archive/`, {
      reason,
      notes
    });
    return response;
  }

  async restoreChatbotSession(sessionId: string): Promise<ArchiveResponse> {
    const response = await api.post<ArchiveResponse>(`/chatbot/sessions/${sessionId}/restore/`);
    return response;
  }

  async bulkArchiveChatbotSessions(
    sessionIds: string[], 
    reason: string = 'bulk_action', 
    notes: string = ''
  ): Promise<BulkArchiveResponse> {
    const response = await api.post<BulkArchiveResponse>('/chatbot/sessions/bulk-archive/', {
      session_ids: sessionIds,
      reason,
      notes
    });
    return response;
  }

  async bulkRestoreChatbotSessions(sessionIds: string[]): Promise<BulkArchiveResponse> {
    const response = await api.post<BulkArchiveResponse>('/chatbot/sessions/bulk-restore/', {
      session_ids: sessionIds
    });
    return response;
  }

  async getArchivedChatbotSessions(params: {
    limit?: number;
    offset?: number;
    search?: string;
    category?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    order?: string;
  } = {}): Promise<ArchiveListResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    const queryString = queryParams.toString();
    const url = queryString ? `/chatbot/sessions/archived/?${queryString}` : '/chatbot/sessions/archived/';
    const response = await api.get<ArchiveListResponse>(url);
    return response;
  }

  async getChatbotArchiveStats(): Promise<ArchiveStatsResponse> {
    const response = await api.get<ArchiveStatsResponse>('/chatbot/sessions/archive-stats/');
    return response;
  }

  async autoRestoreChatbotSession(
    sessionId: string, 
    trigger: string = 'new_activity', 
    context: string = ''
  ): Promise<ArchiveResponse> {
    const response = await api.post<ArchiveResponse>(`/chatbot/sessions/${sessionId}/auto-restore/`, {
      trigger,
      context
    });
    return response;
  }

  // Unified archive operations
  async searchArchived(params: {
    q?: string;
    type?: 'messaging' | 'ai' | 'all';
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
    archive_reason?: string;
    sort_by?: string;
  } = {}): Promise<UnifiedSearchResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    const queryString = queryParams.toString();
    const url = queryString ? `/messages/archive/search/?${queryString}` : '/messages/archive/search/';
    const response = await api.get<UnifiedSearchResponse>(url);
    return response;
  }

  async getArchiveAnalytics(params: {
    period?: number;
    type?: 'messaging' | 'ai' | 'all';
  } = {}): Promise<AnalyticsResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, String(value));
    });
    const queryString = queryParams.toString();
    const url = queryString ? `/messages/archive/analytics/?${queryString}` : '/messages/archive/analytics/';
    const response = await api.get<AnalyticsResponse>(url);
    return response;
  }

  async bulkMixedOperation(operation: 'archive' | 'restore', items: Array<{
    type: 'messaging' | 'ai';
    id: string;
  }>, reason: string = 'bulk_action', notes: string = ''): Promise<{
    success: boolean;
    data: {
      operation: string;
      total_requested: number;
      results: {
        messaging: { successful: number; failed: number; ids: string[] };
        ai: { successful: number; failed: number; ids: string[] };
      };
      errors: string[];
    };
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        operation: string;
        total_requested: number;
        results: {
          messaging: { successful: number; failed: number; ids: string[] };
          ai: { successful: number; failed: number; ids: string[] };
        };
        errors: string[];
      };
    }>('/messages/archive/bulk-operation/', {
      operation,
      items,
      reason,
      notes
    });
    return response;
  }

  async getComplianceReport(days: number = 30): Promise<{
    success: boolean;
    data: {
      analysis_period: string;
      total_operations: number;
      violations: Array<{
        type: string;
        description: string;
        severity: string;
      }>;
      warnings: Array<{
        type: string;
        description: string;
        severity: string;
      }>;
      compliance_score: number;
      recommendations: string[];
    };
  }> {
    const url = `/messages/archive/compliance/?days=${days}`;
    const response = await api.get<{
      success: boolean;
      data: {
        analysis_period: string;
        total_operations: number;
        violations: Array<{
          type: string;
          description: string;
          severity: string;
        }>;
        warnings: Array<{
          type: string;
          description: string;
          severity: string;
        }>;
        compliance_score: number;
        recommendations: string[];
      };
    }>(url);
    return response;
  }
}

export const archiveApi = new ArchiveApiService();